import apiClient from '../utils/apiClient';

/**
 * QC Result API Services (ON-42)
 * Trang tổng hợp/tracking kết quả QC xuyên 4 nguồn (ShiftTask/GameCheck/Loyalty/TablePrep)
 */

export const qcResultAPI = {
  /**
   * Danh sách kết quả QC gộp từ 4 nguồn
   * @param {Object} params - Tham số truy vấn
   * @param {string} [params.branch_id]
   * @param {'SHIFT_TASK'|'GAME_CHECK'|'LOYALTY_REVIEW'|'TABLE_PREP'} [params.source_kind]
   * @param {string} [params.qc_type]
   * @param {'PENDING'|'IN_REVIEW'|'PASSED'|'FAILED'} [params.qc_status]
   * @param {'APPROVED'|'REJECTED_AND_FIXED'} [params.qc_review_result]
   * @param {string} [params.session_id]
   * @param {string} [params.employee_id]
   * @param {string} [params.qc_reviewer_id]
   * @param {'true'|'false'} [params.handled]
   * @param {string} [params.start_date] - YYYY-MM-DD
   * @param {string} [params.end_date] - YYYY-MM-DD
   * @param {'qc_reviewed_at'|'occurred_at'} [params.date_field]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @returns {Promise<Object>} { stats, data, meta }
   */
  getQcResults: async (params = {}) => {
    return await apiClient.get({ url: '/qc-results', params });
  },

  /**
   * Aggregation kết quả QC theo nhân viên, xuyên 4 nguồn
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {string} [params.start_date]
   * @param {string} [params.end_date]
   * @param {'qc_reviewed_at'|'occurred_at'} [params.date_field]
   * @param {string} [params.source_kind]
   * @param {string} [params.session_id]
   * @param {'true'} [params.include_incomplete_sessions] - #180900: tính cả phiên chưa đủ set đánh giá
   * @param {'failed'|'pass_rate'|'total'} [params.sort_by]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @returns {Promise<Object>} { data, meta }
   */
  getQcResultsByEmployee: async (params = {}) => {
    return await apiClient.get({ url: '/qc-results/by-employee', params });
  },

  /**
   * Aggregation kết quả QC theo phiên QC, xuyên 4 nguồn.
   * Phiên chưa ai chấm vẫn có dòng (total = 0).
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {string} [params.start_date] - lọc theo planned_task_date
   * @param {string} [params.end_date] - lọc theo planned_task_date
   * @param {string} [params.source_kind]
   * @param {'OPEN'|'IN_PROGRESS'|'COMPLETED'} [params.status]
   * @param {'AUTO'|'MANUAL'} [params.type]
   * @param {string} [params.qc_employee_id]
   * @param {'true'} [params.evaluation_only] - #180900: chỉ phiên đủ set đánh giá
   * @param {'planned_task_date'|'failed'|'pass_rate'|'total'|'unhandled_failed'} [params.sort_by]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @returns {Promise<Object>} { data, meta }
   */
  getQcResultsBySession: async (params = {}) => {
    return await apiClient.get({ url: '/qc-results/by-session', params });
  },

  /**
   * #175146 — Thống kê chất lượng NGƯỜI CHẤM QC (NV nào chấm ẩu).
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {string} [params.from] - YYYY-MM-DD
   * @param {string} [params.to] - YYYY-MM-DD
   * @returns {Promise<Object>} { data, meta }
   */
  getReviewerQuality: async (params = {}) => {
    return await apiClient.get({ url: '/qc-results/reviewer-quality', params });
  },

  /**
   * #175146 — Danh sách mục QC vào diện HẬU KIỂM.
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {string} [params.qc_employee_id]
   * @param {'true'|'false'} [params.is_mismatch]
   * @param {'true'|'false'} [params.audited]
   * @param {string} [params.from]
   * @param {string} [params.to]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   * @returns {Promise<Object>} { data, meta }
   */
  getQcAudits: async (params = {}) => {
    return await apiClient.get({ url: '/qc-audits', params });
  },

  /**
   * #175146 — Chấm lại 1 mục hậu kiểm. Lệch với kết quả gốc thì ghi nhận và (đủ lần) phạt người chấm.
   * @param {string} id
   * @param {Object} data
   * @param {'PASSED'|'FAILED'} data.audit_status
   * @param {string} [data.note]
   * @returns {Promise<Object>}
   */
  reviewQcAudit: async (id, data) => {
    return await apiClient.post({ url: `/qc-audits/${id}/review`, data });
  },

  /**
   * Chi tiết 1 kết quả QC (đã resolve label tiêu chí)
   * @param {string} sourceKind - SHIFT_TASK | GAME_CHECK | LOYALTY_REVIEW | TABLE_PREP
   * @param {string} id - source_id
   * @returns {Promise<Object>} QcResultRowDto
   */
  getQcResultDetail: async (sourceKind, id) => {
    return await apiClient.get({ url: `/qc-results/${sourceKind}/${id}` });
  },

  /**
   * Đánh dấu admin đã xử lý 1 kết quả QC
   * @param {string} sourceKind
   * @param {string} id
   * @param {Object} data
   * @param {'PENALIZED'|'WARNED'|'COACHED'|'NO_ACTION'|'DISPUTED'} data.resolution
   * @param {string} [data.note]
   * @returns {Promise<Object>} handling row
   */
  handleQcResult: async (sourceKind, id, data) => {
    return await apiClient.post({ url: `/qc-results/${sourceKind}/${id}/handle`, data });
  },

  /**
   * Bỏ đánh dấu đã xử lý (giữ lại ghi chú)
   * @param {string} sourceKind
   * @param {string} id
   * @returns {Promise<Object>} handling row
   */
  unhandleQcResult: async (sourceKind, id) => {
    return await apiClient.post({ url: `/qc-results/${sourceKind}/${id}/unhandle` });
  },

  /**
   * Gắn điểm thưởng/phạt cho nhân viên liên quan đến kết quả QC
   * @param {string} sourceKind
   * @param {string} id
   * @param {Object} data
   * @param {'ADD'|'SUBTRACT'} data.type
   * @param {number} data.value - >= 1
   * @param {string} data.reason
   * @returns {Promise<Object>} handling row (kèm point)
   */
  applyQcPoint: async (sourceKind, id, data) => {
    return await apiClient.post({ url: `/qc-results/${sourceKind}/${id}/point`, data });
  },

  /**
   * Gỡ điểm đã gắn (huỷ Point, không xoá lịch sử)
   * @param {string} sourceKind
   * @param {string} id
   * @returns {Promise<Object>} handling row
   */
  revokeQcPoint: async (sourceKind, id) => {
    return await apiClient.delete({ url: `/qc-results/${sourceKind}/${id}/point` });
  },
};
